/**
 * 子图遍历 —— 纯函数。
 *
 * 组和循环节点内部还有一张图，而子图还能再套子图。所以「遍历所有层级的节点」
 * 这件事会出现在很多地方（校验、序列化、查找、统计），必须只有一份实现。
 *
 * ## 两件必须防住的事
 *
 * 1. **自含**：一个节点的子图里如果又出现它自己（同 id），
 *    遍历和求值都会无限递归。JSON 里构造不出来，但内存里可以
 *    （同一个对象被塞进自己的 graph）。
 * 2. **深度**：光防自含不够，A 里装 B、B 里装 A 也是环。
 *    所以遍历带一个**深度上限**兜底 —— 到了就停，不抛错。
 */
import {
  GROUP_OUTPUT_NODE,
  GROUP_OUT_REPEATER,
  groupRowsFromGraph,
  groupInterfaceOf,
  syncInputNode,
} from './group'
import { connectedRepeaterCount } from './rows'
import {
  LOOP_OUT_NODE,
  innerParamCount,
  loopRowsFromGraph,
  syncStartNode,
} from './loop'
import type { SocketType } from '../types/socket'
import type { GraphNodeModel, SubGraph, SubGraphEdge } from '../types/node'

/** 子图嵌套的深度上限。到了就停 —— 防环，也防有人手搓出病态数据 */
export const MAX_SUBGRAPH_DEPTH = 8

/**
 * **子图接口节点的类型** —— 组 / 循环内部那两个"端口面板"节点。
 *
 * ```text
 *   group.input / group.output    组的输入 / 输出面板
 *   loop.start / loop.output      循环的起始 / 结果累记
 * ```
 *
 * 它们的共同点：**属于自己那个子图**，不该被当成普通节点搬来搬去 ——
 * 收进别的组、删掉、当成建组的成员，都会让接口的镜像关系断掉。
 *
 * ⚠️ 这份名单原来在 `useSubgraphLevels` 里叫 `PROTECTED_TYPES`，
 * 而 `useGrouping` 里写了**另一份错的**判断：拿 `GROUP_INPUT_NODE`
 * 这个 **id 常量**（`'__group_in'`）去比 `model.type`（`'group.input'`）——
 * 永远不成立，于是"接口节点不许收进组"这条规则**从来没生效过**。
 * 现在两处共用这一个常量，类型是唯一的判据。
 */
export const SUBGRAPH_INTERFACE_TYPES = [
  'group.input',
  'group.output',
  'loop.start',
  'loop.output',
] as const

/** 是不是子图接口节点（按**类型**判 —— id 那份常量是 `__group_in` 之类） */
export function isSubgraphInterfaceType(type: string): boolean {
  return (SUBGRAPH_INTERFACE_TYPES as readonly string[]).includes(type)
}

/**
 * 深度优先遍历所有层级的节点。
 *
 * `path` 是从顶层下来的**节点 id 链**（不含自己）—— 用来定位
 * 「这个问题出在哪个组里面」。顶层节点的 path 是空数组。
 */
export function walkModels(
  models: readonly GraphNodeModel[],
  visit: (model: GraphNodeModel, path: readonly string[]) => void,
  path: readonly string[] = [],
  depth = 0,
  seen: Set<GraphNodeModel> = new Set(),
): void {
  if (depth > MAX_SUBGRAPH_DEPTH) return

  for (const model of models) {
    // 同一个对象再次出现 = 自含或环，直接跳过
    if (seen.has(model)) continue
    seen.add(model)

    visit(model, path)

    if (model.graph) {
      walkModels(model.graph.nodes, visit, [...path, model.id], depth + 1, seen)
    }
  }
}

/** 子图里出现过的所有节点 id（含更深层） */
export function collectSubgraphIds(graph: SubGraph, depth = 0): string[] {
  if (depth > MAX_SUBGRAPH_DEPTH) return []
  const ids: string[] = []
  for (const node of graph.nodes) {
    ids.push(node.id)
    if (node.graph) ids.push(...collectSubgraphIds(node.graph, depth + 1))
  }
  return ids
}

/**
 * 这个 id 是否出现在子图里（含更深层）。
 *
 * 用来查「一个节点的子图里有没有它自己」—— 那是无限递归。
 */
export function graphContainsId(graph: SubGraph, id: string): boolean {
  return collectSubgraphIds(graph).includes(id)
}

/** 层数统计（给「嵌套太深了」的提示用） */
export function graphDepth(models: readonly GraphNodeModel[], depth = 0): number {
  if (depth > MAX_SUBGRAPH_DEPTH) return depth
  let max = depth
  for (const model of models) {
    if (!model.graph) continue
    max = Math.max(max, graphDepth(model.graph.nodes, depth + 1))
  }
  return max
}

/** 整个图里有多少个节点（含所有层级）—— 导出和统计都会用到 */
export function countNodes(models: readonly GraphNodeModel[]): number {
  let total = 0
  walkModels(models, () => {
    total += 1
  })
  return total
}

/**
 * 找出所有「子图里含自己」的节点 id。
 *
 * 这类数据不该出现（正常操作造不出来），但外部载入的 JSON 可能有，
 * 所以校验层要能报出来，而不是等到求值时栈溢出。
 */
export function findSelfContaining(models: readonly GraphNodeModel[]): string[] {
  const bad: string[] = []
  walkModels(models, (model) => {
    if (model.graph && graphContainsId(model.graph, model.id)) bad.push(model.id)
  })
  return bad
}

/** 内层节点 id → 位置。缺位置时给一个可用的默认值 */
export function positionOf(graph: SubGraph, nodeId: string): { x: number; y: number } {
  return graph.positions?.[nodeId] ?? { x: 0, y: 0 }
}

/**
 * 给**没有位置**的节点补上位置，并让新加的接口节点避开已有内容。
 *
 * 为什么需要：子图里的坐标是各层独立的，而「组从选区建出来」时
 * 我只记了原有节点的位置 —— 两个接口节点没位置，于是都落在 (0,0)
 * **叠在一起**，进去看到的是一个压着一个的节点。
 *
 * `anchors` 是「这个 id 应该放哪」的偏好（接口节点在左右两边），
 * 但会先避开已有内容：内容整体往右挪了，锚点也跟着挪。
 */
export function fillMissingPositions(
  graph: SubGraph,
  anchors: Record<string, { x: number; y: number }> = {},
): void {
  const known = { ...(graph.positions ?? {}) }

  // 已有内容的包围盒 —— 锚点要相对它算，不能写死绝对坐标
  const placed = Object.values(known)
  const minX = placed.length ? Math.min(...placed.map((p) => p.x)) : 0
  const minY = placed.length ? Math.min(...placed.map((p) => p.y)) : 0
  const maxX = placed.length ? Math.max(...placed.map((p) => p.x)) : 0

  let column = 0
  for (const node of graph.nodes) {
    if (known[node.id]) continue

    const anchor = anchors[node.id]
    if (anchor) {
      /*
       * 锚点是**相对已有内容**的偏移：
       *   负 x → 放到最左边那列的左侧；正 x → 放到最右边那列的右侧
       * 这样不管原来的节点摆在哪，接口节点都在它们的左右两边。
       */
      const x = anchor.x < 0 ? minX - 280 : maxX + 260
      known[node.id] = { x, y: minY + anchor.y }
      continue
    }

    // 其余没位置的节点：排成一列，别叠着
    known[node.id] = { x: minX, y: minY + 230 + column * 230 }
    column += 1
  }

  graph.positions = known
}

/* ══════════════════════════════════════════════════════════════
   进子图编辑时对齐接口
   ══════════════════════════════════════════════════════════════ */

/** 内部某个变参行上**已经确认出来的**实例类型（反推外部端口时带上） */
function instanceTypesOf(
  graph: SubGraph,
  nodeId: string,
  rowId: string,
): readonly (SocketType | undefined)[] | undefined {
  const node = graph.nodes.find((n) => n.id === nodeId)
  const row = node?.rows.find((r) => r.kind === 'repeater' && r.id === rowId)
  return row?.kind === 'repeater' ? row.instanceTypes : undefined
}

/**
 * 把**组**的内部接口对齐到外部现状，并反推外部的行。
 *
 * ★★ 这里的规矩是用户定下来的（他在资产上发现了不对）：
 *
 * > 输入节点靠外部的变参行输入是否不符合需求，应该和输出节点一样，
 * > 是**内部数量决定外部数量**才对 —— 不然内部图是 ok 的，
 * > 但是外部如果没有传入参数，里边的入参连线不就断了吗
 *
 * 完全正确，所以现在**两个方向对称**了：
 *
 *   输入 —— `max(内部声明, 外部使用)`：内部是**基准**，外部接线只能往上加。
 *   输出 —— 内部接了几路，外面就显示几个端口（本来就是这样）。
 *
 * ⚠️ 取 `max` 而不是直接用「内部声明」：外部连了一个**新**参数时，
 * 内部得跟着长出来（否则外面连了、里面接不上）。
 * ⚠️ 也不是直接用「外部使用」：那正是原来的 bug —— 自带接口的**资产**
 * 在外部还没接线时一进组，内部接口行被清空，里面接好的线全部
 * 指向不存在的端口（Vue Flow 报 `EDGE_SOURCE_TARGET_MISSING`）。
 *
 * 「外面一个都没连就是 0 行」那条**仍然成立**：新建的组在打组时
 * 内部行数就按当时的入边建好了（0 条就是 0 行），`max(0, 0)` 还是 0。
 */
export function refreshGroupInterface(
  model: GraphNodeModel,
  /**
   * 外面连上之后**确认出来的类型**（按序号）。
   *
   * ⚠️ 类型和数量的流向是**分开**的：
   *   · 数量 —— 里面说了算（外面连不连都改不了组的结构）；
   *   · 类型 —— 外面连了什么就确认成什么（类型不是结构，
   *     而且组内的连线校验要靠它，否则组里全是 `any`）。
   */
  inputTypes?: readonly (SocketType | undefined)[],
): void {
  const graph = model.graph
  if (!graph) return

  /*
   * ★★ **数量只认内部声明** —— 这里原来写 `Math.max(内部声明, 外面连了几路)`，
   * 也就是外面接线能把组撑大。
   *
   * 用户否掉了那个方向：
   *   「组节点需要全部改成外部静态、内层变参行，防止因为外部改变组内部的结构」
   *
   * 所以外面**不再参与数量的推导**：参数几路由组内部的接口节点说了算
   * （用户在组里点「＋」加一路），外面看到的端口只是它的镜像。
   */
  const decl = groupInterfaceOf(graph)

  /*
   * ⚠️⚠️ 这里**只能**把「变参那几路」交给 `syncInputNode` ——
   * 那个数会被写进**变参行**的 `min`。
   *
   * 上一轮传的是 `innerInterfaceCount()`（**固定 + 变参的总数**），于是
   * 内部固定参数的条数被算进了变参行 → **每进一次组、每出一次组，
   * 变参就长一路**（用户报的正是「进入再出来，外部就会多参数」）。
   * 固定参数与变参是两组东西，只有变参那个数归变参行。
   */
  syncInputNode(graph, decl.variadic, inputTypes)
  model.rows = groupRowsFromGraph(
    graph,
    decl.variadic,
    instanceTypesOf(graph, GROUP_OUTPUT_NODE, GROUP_OUT_REPEATER),
    inputTypes,
  )
}

/**
 * 把**循环**的内部参数对齐到外部现状（规矩同 `refreshGroupInterface`）。
 *
 * 循环那边原来也是「外面连了几个就用几个」，是同一个 bug 的另一个出口 ——
 * 一次修两处，不然下次换个节点类型又踩一遍。
 */
export function refreshLoopInterface(
  model: GraphNodeModel,
  inputTypes?: readonly (SocketType | undefined)[],
): void {
  const graph = model.graph
  if (!graph) return

  /* ★ 同组：参数几路由**里面**的起始节点说了算（见上） */
  const params = innerParamCount(graph)

  syncStartNode(graph, params, inputTypes)
  /* ⚠️ 循环结果行的 id 是字面量 `results`（见 `loopOutputRows`），不是常量 */
  model.rows = loopRowsFromGraph(graph, params, instanceTypesOf(graph, LOOP_OUT_NODE, 'results'))
}

/** 子图里有没有边指向不存在的节点 —— 载入外部数据时的常见损坏 */
export function danglingEdges(graph: SubGraph): SubGraphEdge[] {
  const ids = new Set(collectSubgraphIds(graph))
  return graph.edges.filter(
    (edge) => !ids.has(edge.source) || !ids.has(edge.target),
  )
}
